import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { getItemType } from './itemTypes';

const DELIMITER = /[\s\-_:./]/;

// First word of a name: "writingPage-intro" -> "writing", "HomePage" -> "Home", "page2" -> "page"
const firstWord = (name) => {
  const match = name.match(/^(?:[0-9]+|[A-Za-z]+?(?=[A-Z][a-z]|[^A-Za-z]|$))/);
  return match ? match[0] : name;
};

// Whether a new word starts at index i: a delimiter, camelCase hump, trailing number, or the end
const startsWord = (name, i) => {
  if (i >= name.length || DELIMITER.test(name[i])) return true;
  const prev = name[i - 1];
  return (/[A-Z]/.test(name[i]) && /[a-z0-9]/.test(prev)) || (/[0-9]/.test(name[i]) && /[A-Za-z]/.test(prev));
};

// Label a group by the longest whole-word prefix its names share, e.g. "writingPage"
const groupLabel = (names) => {
  const [first] = names;
  const sharedAt = (i) => names.every((name) => name[i] === first[i]);
  const allStartWordAt = (i) => names.every((name) => startsWord(name, i));

  let length = 0;
  while (length < first.length && sharedAt(length)) length += 1;
  while (length > 0 && !allStartWordAt(length)) length -= 1;
  return first.slice(0, length).replace(/[\s\-_:./]+$/, '') || firstWord(first);
};

// Bucket items by first word so related pages sit together with a count
const groupItems = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = firstWord((item.displayName || '').trim()).toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  return Array.from(groups, ([key, groupedItems]) => ({
    key,
    label: groupLabel(groupedItems.map((item) => (item.displayName || '').trim())),
    items: groupedItems,
  }));
};

const matchesSearch = (item, query) => (
  `${item.displayName} ${getItemType(item.itemType).searchText(item)}`.toLowerCase().includes(query)
);

const ItemRow = ({ item, active, onSelect }) => {
  const type = getItemType(item.itemType);

  return (
    <button
      type="button"
      onClick={() => onSelect(item._id)}
      aria-current={active ? 'true' : undefined}
      className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm transition-colors ${
        active ? 'bg-blue-600 text-white shadow' : 'text-gray-200 hover:bg-gray-800'
      }`}
    >
      <FontAwesomeIcon
        icon={type.icon}
        className={`w-3.5 shrink-0 ${active ? 'text-blue-100' : 'text-gray-400'}`}
      />
      <span className="flex-1 truncate" title={item.displayName}>{item.displayName}</span>
      {type.count && (
        <span className={`text-xs ${active ? 'text-blue-100' : 'text-gray-400'}`}>
          {type.count(item)}
        </span>
      )}
    </button>
  );
};

const ConsoleSidebar = ({ items, selectedId, onSelect }) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());

  const query = search.trim().toLowerCase();
  const groups = useMemo(
    () => groupItems(query ? items.filter((item) => matchesSearch(item, query)) : items),
    [items, query]
  );

  const toggleGroup = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <>
      <div className="border-b border-gray-700/70 p-3">
        <div className="relative">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${items.length} ${items.length === 1 ? 'item' : 'items'}…`}
            aria-label="Search items"
            className="w-full rounded-md bg-gray-800 py-2 pl-9 pr-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-2" aria-label="Console items">
        {groups.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-gray-400">
            {query ? `No items match "${search.trim()}"` : 'No items yet'}
          </p>
        )}

        {groups.map((group) => {
          if (group.items.length === 1) {
            const [item] = group.items;
            return <ItemRow key={item._id} item={item} active={item._id === selectedId} onSelect={onSelect} />;
          }

          const open = Boolean(query) || !collapsed.has(group.key);
          return (
            <div key={group.key} className="pt-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={open}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold text-gray-300 hover:bg-gray-800 hover:text-white"
              >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="w-3 text-gray-500" />
                <span className="flex-1 truncate text-left">{group.label}</span>
                <span className="rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-200">
                  {group.items.length}
                </span>
              </button>
              {open && (
                <div className="ml-3.5 mt-0.5 space-y-0.5 border-l border-gray-700 pl-2">
                  {group.items.map((item) => (
                    <ItemRow key={item._id} item={item} active={item._id === selectedId} onSelect={onSelect} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );
};

export default ConsoleSidebar;
